import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import {
  getAverageMessages,
  getAverageSatisfaction,
  getAverageSeconds,
  getConversationsPerMonth,
  getTotalConversations,
  getTotalMessages,
  getUniqueChatConversations,
} from '../../helpers/chatDashboardHelper';
import { getCountsPerDay, getTotalRows } from '../../helpers/dashboardHelper';
import { useTranslation } from '../useTranslation';
import { AgentType } from '../../interfaces/Agent';
import { ChatAgent } from '../../interfaces/ChatAgent';
import { Conversation, DateRangeDate } from '../../interfaces/Analytics';
import { useConversationsTableState } from '../useConversationsTableState';
import { useIsRefreshing } from '../useIsRefreshing';
import { useChatDashboardData } from './useChatDashboardData';
import { useDashboardChatAgents } from './useDashboardChatAgents';

const fillOutAgentNames = (
  conversations: Conversation[],
  agentsList: ChatAgent[],
  unknownAgentLabel: string
): Conversation[] => {
  return conversations.map(conversation => {
    const agent = agentsList.find(agent => agent.id === conversation.agent_id);
    return {
      ...conversation,
      agent_name: agent?.name ?? unknownAgentLabel,
    };
  });
};

/**
 * Hook for single chat agent dashboard (used in ShowChatAgent page)
 * Provides chat-specific metrics and conversations data
 */
export const useSingleChatAgentDashboard = (
  companyId: string | undefined,
  userId: string | undefined,
  agentId: string | undefined
) => {
  const { t } = useTranslation();

  // Set the default date range to the last 15 days
  const [dateRange, setDateRange] = useState<[DateRangeDate, DateRangeDate]>([
    dayjs().subtract(14, 'day'),
    dayjs(),
  ]);

  const table = useConversationsTableState(AgentType.CHAT);
  const changeDateRange = (range: [DateRangeDate, DateRangeDate]) => {
    setDateRange(range);
    table.backToFirstPage();
  };

  // Fetch chat agent details
  const { data: agentsList } = useDashboardChatAgents(!!agentId);

  // Fetch chat dashboard data for single agent
  const {
    data: rawDashboardData,
    refetch,
    isLoading,
    isFetching,
    error,
  } = useChatDashboardData({
    companyId,
    userId,
    startDate: dateRange[0]?.format('YYYY-MM-DD'),
    endDate: dateRange[1]?.format('YYYY-MM-DD'),
    agentIds: agentId,
    enabled: !!agentId,
    paging: table.paging,
  });

  // The cards only change with the range, not with a page, sort or search
  const isRefreshingMetrics = useIsRefreshing(
    `${dateRange[0]?.format('YYYY-MM-DD')}|${dateRange[1]?.format('YYYY-MM-DD')}`,
    isFetching
  );

  // Calculate chat-specific metrics from dashboard data
  const chatMetrics = useMemo(() => {
    if (!rawDashboardData) {
      return {
        totalConversations: 0,
        totalMessages: 0,
        averageSatisfaction: 0,
        averageSeconds: 0,
        averageMessages: 0,
      };
    }
    return {
      totalConversations: getTotalConversations(rawDashboardData),
      totalMessages: getTotalMessages(rawDashboardData),
      averageSatisfaction: getAverageSatisfaction(rawDashboardData),
      averageSeconds: getAverageSeconds(rawDashboardData),
      averageMessages: getAverageMessages(rawDashboardData),
    };
  }, [rawDashboardData]);

  // Calculate conversations per month
  const conversationsPerMonth = useMemo(() => {
    if (!rawDashboardData) {
      return {};
    }
    return getConversationsPerMonth(rawDashboardData);
  }, [rawDashboardData]);

  const conversationsPerDay = useMemo(
    () => getCountsPerDay(rawDashboardData, 'daily_conversations_number'),
    [rawDashboardData]
  );

  // Get conversations with agent names
  const conversations = useMemo(() => {
    if (!rawDashboardData || !agentsList) {
      return [];
    }
    return fillOutAgentNames(
      getUniqueChatConversations(rawDashboardData),
      agentsList,
      t('Unknown Agent')
    );
  }, [rawDashboardData, agentsList, t]);

  return {
    chatMetrics,
    conversationsPerMonth,
    conversationsPerDay,
    conversations,
    dateRange,
    isRefreshingMetrics,
    // For live sync
    refetch,
    isLoading,
    isFetching,
    error,
    setDateRange: changeDateRange,
    serverPaging: table.serverPaging(
      getTotalRows(rawDashboardData, chatMetrics.totalConversations),
      isFetching
    ),
    paginationModel: table.paginationModel,
    setPaginationModel: table.setPaginationModel,
    setSortModel: table.setSortModel,
    setSearch: table.setSearch,
  };
};
